import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type StatusBadgeVariant =
  | "success"
  | "warning"
  | "danger"
  | "info"
  /**
   * Something the viewer can act on that is not yet theirs — an RFQ open for
   * bidding, say. Distinct from `info` on purpose: `info` marks work already
   * assigned and in progress, and rendering an opportunity in the same blue
   * hides the one row on the screen where the professional can do something.
   */
  | "opportunity"
  /**
   * A benign terminal state — finished, nothing owed, nobody at fault. Grey on
   * purpose: "sin respuesta" after a full reminder cadence is a non-event, and
   * rendering it in red (the only other terminal tone) reads as a failure the
   * operator should act on.
   */
  | "neutral"
  | "softDanger";

const VARIANT_CLASS: Record<StatusBadgeVariant, string> = {
  success:
    "border-green-500/20 bg-green-500/10 text-green-700 dark:text-green-400",
  warning:
    "border-yellow-500/20 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400",
  danger: "border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-400",
  info: "border-blue-500/20 bg-blue-500/10 text-blue-700 dark:text-blue-400",
  // Teal — the brand accent's family, and the only hue not already carrying a
  // status meaning. Text is teal-700 rather than the accent itself: #19b5a6 on
  // a 10% tint is about 2.5:1, which fails on badge-sized text.
  opportunity: "border-teal-500/25 bg-teal-500/10 text-teal-700 dark:text-teal-300",
  neutral:
    "border-border bg-muted text-muted-foreground",
  softDanger:
    "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400",
};

export interface StatusBadgeProps {
  variant: StatusBadgeVariant;
  children: React.ReactNode;
  className?: string;
}

export function StatusBadge({
  variant,
  children,
  className,
}: StatusBadgeProps): JSX.Element {
  return (
    <Badge
      variant="outline"
      className={cn(VARIANT_CLASS[variant], className)}
    >
      {children}
    </Badge>
  );
}
